import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  useGetPreSignedUrlForUpload,
  useUploadFile,
  useDeleteFile,
} from '@/lib/api/hooks/use-storage-file';
import { getFileByFileId } from '@/lib/api/services/storage.service';
import { useUpdateAgentPersona, useUpdateWidget } from '@/modules/gpt-chats/hooks/use-agents';
import { Agent } from '@/modules/gpt-chats/types/agent.service.type';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { isErrorWithErrors } from '@/lib/error';
import emptyProfilePhoto from '@/assets/images/widget.png';
import { ModuleName } from '@/constant/modules.constants';

type AgentImageUploaderProps = {
  agent: Agent;
};

export const AgentImageUploader = ({ agent }: AgentImageUploaderProps) => {
  const { t } = useTranslation();
  const tenantId = import.meta.env.VITE_X_BLOCKS_KEY || '';
  const [image, setImage] = useState<string | null>(agent?.logo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: getPreSign } = useGetPreSignedUrlForUpload();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: deleteFile } = useDeleteFile();

  const { mutateAsync: updateAgent } = useUpdateAgentPersona();
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const uploadImage = async (file: File) => {
    try {
      setIsUploading(true);

      const presign = await getPreSign({
        accessModifier: 'Public',
        configurationName: 'Default',
        name: file.name,
        projectKey: tenantId ?? '',
        tags: '',
        metaData: '',
        parentDirectoryId: '',
        moduleName: ModuleName.AICloud,
      });

      if (!presign.isSuccess || !presign.fileId || !presign.uploadUrl) {
        throw new Error(t('FAILED_TO_GET_UPLOAD_URL'));
      }

      await uploadFile({ url: presign.uploadUrl, file });

      const fileInfo = await getFileByFileId({
        itemId: presign.fileId,
        projectKey: tenantId || '',
      });

      const payload: any = {
        ...agent,
        logo_id: presign.fileId,
        logo_url: fileInfo.url,
        agent_id: agent.id,
        agent_type: agent.agent_type || '',
      };

      const res = await updateAgent(payload);
      if (!res.is_success) {
        throw new Error(res.detail || t('FAILED_TO_UPDATE_AGENT_PERSONA'));
      }

      await updateWidget({
        agent_id: agent.id,
        regen_widget_id: false,
        widget: { ...agent.widget, logo_id: presign.fileId ?? null, logo_url: fileInfo.url },
      });

      setImage(fileInfo.url);
      showSuccessToast({ description: t('AGENT_IMAGE_CHANGED_SUCCESSFULLY') });

      if (agent.logo_id) {
        await deleteFile({
          fileId: agent.logo_id,
          projectKey: import.meta.env.VITE_X_BLOCKS_KEY || '',
        });
      }
    } catch (err: unknown) {
      if (isErrorWithErrors(err)) return showErrorToast({ errors: err.errors });

      showErrorToast({ errors: t('SOMETHING_WENT_WRONG_UPLOADING_IMAGE') });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showErrorToast({ errors: t('ONLY_IMAGE_FILES_ALLOWED') });
      return;
    }

    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      showErrorToast({ errors: t('IMAGE_SIZE_LIMIT_ERROR', { size: 5 }) });
      return;
    }

    setImage(URL.createObjectURL(file));
    uploadImage(file);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col items-center justify-center gap-5 lg:col-span-3 lg:justify-start">
      <div className="relative aspect-square h-40 w-40 overflow-hidden rounded-full">
        <img
          src={image || emptyProfilePhoto}
          alt={agent.name || t('AGENT_PROFILE')}
          className="rounded-full object-cover w-full h-full"
        />
        {isUploading && <div className="absolute inset-0 bg-gray-50/75 dark:bg-gray-800/75" />}
      </div>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isUploading}
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
      >
        <Camera className="h-5 w-5" />
        <span className="ml-2.5">{isUploading ? t('UPLOADING') : t('CHANGE_IMAGE')}</span>
      </Button>
    </div>
  );
};
